import { useEffect, useRef, useState } from "react";
import "../ComponentsStyling/Home.css";
import { useReveal, useParallax } from "../utils/useReveal";

/**
 * Home — hero section with an animated typed role, particle backdrop, and parallax blobs.
 *
 * Renders separate desktop and mobile layouts (toggled via CSS) plus reveal/parallax
 * effects. The typed headline cycles through ROLES; an entrance flag staggers content in.
 */

// Job titles cycled through by the typing animation.
const ROLES = [
  "Frontend Developer",
  "React Developer",
  "Software Engineer Intern",
  "Freelance Web Developer",
];

/* Typewriter effect: types each word, pauses, deletes, then advances.

   `active` gates the timer chain. Every keystroke is a setState, so it
   re-renders the whole hero — and the loop used to keep running with the hero
   scrolled off-screen or the tab in the background, measured at 33 mutations
   in 4 seconds several viewports below the fold. The particle canvas already
   stopped itself under those conditions; this did not.

   Reduced motion gets the first role typed out in full and no animation at
   all, rather than a caret blinking through four strings indefinitely. */
function useTyped(words, { speed = 68, pause = 2400, active = true } = {}) {
  const [wordIdx, setWordIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const still =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (still || !active) return;
    const current = words[wordIdx];
    let t;
    if (!deleting && charIdx < current.length) {
      t = setTimeout(() => setCharIdx(c => c + 1), speed);
    } else if (!deleting && charIdx === current.length) {
      t = setTimeout(() => setDeleting(true), pause);
    } else if (deleting && charIdx > 0) {
      t = setTimeout(() => setCharIdx(c => c - 1), speed / 2.5);
    } else {
      setDeleting(false);
      setWordIdx(i => (i + 1) % words.length);
    }
    return () => clearTimeout(t);
  }, [charIdx, deleting, wordIdx, words, speed, pause, active, still]);

  // Derived rather than stored: holding this in state meant a second setState,
  // and so a second render, on every keystroke.
  return still ? words[0] : words[wordIdx].slice(0, charIdx);
}

/* True while `ref` is on screen AND the tab is in the foreground. Used to stop
   the typewriter's timer chain when nobody can see it. */
function useAwake(ref) {
  const [awake, setAwake] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let onScreen = true;
    const sync = () => setAwake(onScreen && !document.hidden);

    const io = new IntersectionObserver(
      ([e]) => { onScreen = e.isIntersecting; sync(); },
      { threshold: 0 }
    );
    io.observe(el);
    document.addEventListener("visibilitychange", sync);
    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", sync);
    };
  }, [ref]);

  return awake;
}

// Decorative canvas: drifting points connected by lines when close enough.
function ParticleCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    // Purely decorative, so skip it outright for reduced-motion users — that
    // also removes a permanently running rAF loop on those machines.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = canvas.getContext("2d");
    let raf = null;
    let W = 0, H = 0;
    const pts = [];
    const N = 28;

    // Size the backing store to the device pixel ratio so the points stay
    // crisp on retina screens, and only respawn when the CSS box actually
    // changed. Mobile browsers fire resize continuously as the URL bar
    // hides and shows; respawning on each one made the field visibly jump.
    function resize() {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      if (w === W && h === H) return;
      W = w; H = h;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      pts.length = 0;
      for (let i = 0; i < N; i++) {
        pts.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.18,
          vy: (Math.random() - 0.5) * 0.18,
          r: Math.random() * 1.1 + 0.3,
        });
      }
    }

    // Animation loop: advance points, bounce off edges, link nearby pairs, then draw dots.
    function draw() {
      ctx.clearRect(0, 0, W, H);
      for (const p of pts) {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;
      }
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(59,130,246,${0.06 * (1 - d / 120)})`;
            ctx.lineWidth = 0.6;
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }
      for (const p of pts) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(59,130,246,0.12)";
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    }

    function start() { if (raf === null) raf = requestAnimationFrame(draw); }
    function stop()  { if (raf !== null) { cancelAnimationFrame(raf); raf = null; } }

    resize();
    start();
    window.addEventListener("resize", resize);

    // Stop burning frames once the hero is scrolled past, or the tab is hidden.
    const io = new IntersectionObserver(([e]) => (e.isIntersecting ? start() : stop()), { threshold: 0 });
    io.observe(canvas);
    const onVisibility = () => (document.hidden ? stop() : start());
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stop();
      io.disconnect();
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return <canvas ref={ref} className="home__canvas" aria-hidden="true" />;
}

export default function Home() {
  const [ready, setReady] = useState(false);
  /* Observed on the <section>, not on either layout wrapper — one of those is
     always `display: none`, and a hidden element never intersects. */
  const sectionRef = useRef(null);
  const role = useTyped(ROLES, { active: useAwake(sectionRef) });

  // Scroll-driven parallax/scale for the hero blobs. Returns a ref callback —
  // the transform is written to the DOM directly so scrolling costs no renders.
  const parallaxRef = useParallax(38, 0.045);
  // Scroll-reveal trigger for the hero content.
  const [heroRef, heroIn] = useReveal({ threshold: 0.18 });

  // Defer the entrance animation a tick so the initial paint isn't mid-transition.
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 80);
    return () => clearTimeout(t);
  }, []);

  /* Headline stat counters (number + label).
     "Projects" must match the roster in RecentProjects.jsx — it read 7 after
     three projects were removed from that section. */
  const stats = [
    { n: "2+", l: "Yrs Frontend" },
    { n: "IEEE", l: "CIS Chapter" },
    { n: "5", l: "Projects" },
  ];

  return (
    <section className="home" ref={sectionRef}>
      <ParticleCanvas />
      {/* Parallax background blobs */}
      <div className="home__blob home__blob--a" aria-hidden="true" ref={parallaxRef} />
      <div className="home__blob home__blob--b" aria-hidden="true" ref={parallaxRef} />

      {/* Desktop + tablet: two-column layout */}
      <div
        className={`home__layout${ready ? " home__layout--in" : ""}`}
        ref={heroRef}
      >
        {/* Left column: name and credentials */}
        <div className="home__left">
          <h1 className="home__name" style={{ "--d": "0.06s" }}>
            <span className="home__name-hi">Hi, I'm</span>
            Khaled
          </h1>

          <div className="home__vline" style={{ "--d": "0.1s" }} aria-hidden="true" />

          <div className="home__cred" style={{ "--d": "0.14s" }}>
            <span>University of Jordan</span>
            <span className="home__cred-dot" aria-hidden="true" />
            <span>Shabakkat KSA</span>
            <span className="home__cred-dot" aria-hidden="true" />
            <span>Present</span>
          </div>
        </div>

        {/* Right column: role, bio, stats, and CTAs */}
        <div className="home__right">
          <div className="home__avail" style={{ "--d": "0.08s" }}>
            <span className="avail">Available for freelance</span>
          </div>

          <div className="home__sub" style={{ "--d": "0.14s" }}>
            Frontend Developer building{" "}
            <em className="home__sub-em">real web experiences</em>
          </div>

          {/* Decorative typewriter. This was an aria-live region, so screen
              readers re-announced the line on every single character — an
              endless stream of speech while the page is open. The roles are
              already stated in .home__sub above, so it is hidden from AT. */}
          <div className="home__role" style={{ "--d": "0.2s" }} aria-hidden="true">
            <span className="home__role-arrow">→</span>
            <span className="home__role-text">{role}</span>
            <span className="home__role-cursor" />
          </div>

          <div className="home__rule" style={{ "--d": "0.26s" }} aria-hidden="true" />

          {/* "and startups" was dropped: nothing in Worked With is a startup,
              so it was the one unevidenced claim in the line. */}
          <p className="home__bio" style={{ "--d": "0.3s" }}>
            Software Engineer Intern at{" "}
            <strong>Shabakkat KSA</strong>. I build fast, accessible, responsive
            interfaces in React — and ship real, deployed websites for
            organizations, companies, and events, including{" "}
            <strong>IEEE CIS UJ</strong>, <strong>TEDx UJ</strong>, and{" "}
            <strong>AI Expo</strong>.
          </p>

          <div className="home__stats" style={{ "--d": "0.36s" }}>
            {stats.map(({ n, l }, i) => (
              <div key={l} className="home__stat">
                <span className="home__stat-n">{n}</span>
                <span className="home__stat-l">{l}</span>
                {i < stats.length - 1 && <span className="home__stat-sep" aria-hidden="true" />}
              </div>
            ))}
          </div>

          <div className="home__ctas" style={{ "--d": "0.42s" }}>
            <a href="#work" className="btn btn--primary">
              View My Work
              <svg className="btn__icon btn__icon--arrow" viewBox="0 0 16 16"
                fill="none" aria-hidden="true">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor"
                  strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <a href="#contact" className="btn btn--secondary">
              Get in Touch
            </a>
            <a href="/CV.pdf" target="_blank" rel="noopener noreferrer"
              className="btn btn--ghost" download>
              <svg className="btn__icon btn__icon--down" viewBox="0 0 18 18"
                fill="none" aria-hidden="true">
                <path d="M9 3v8M6 8l3 3 3-3M4 14h10" stroke="currentColor"
                  strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Download CV
            </a>
          </div>
        </div>
      </div>

      {/* Mobile layout: separate DOM, shown only at <=760px via CSS */}
      <div className={`home__mobile ${ready ? "home__mobile--in" : ""}`}>

        {/* Top bar */}
        <div className="home__m-bar">
          <span className="avail">Available for freelance</span>
        </div>

        {/* Giant name — fills width */}
        <div className="home__m-name-wrap">
          <p className="home__m-greeting">Hi, I'm</p>
          <h1 className="home__m-name">Khaled</h1>
        </div>

        {/* Role subtitle */}
        <div className="home__m-sub">
          Frontend Developer &amp; <em>React Specialist</em>
        </div>

        {/* Typed line */}
        {/* Decorative — see the note on the desktop typewriter above. */}
        <div className="home__m-role" aria-hidden="true">
          <span className="home__m-role-arrow">→</span>
          <span className="home__m-role-text">{role}</span>
          <span className="home__m-cursor" />
        </div>

        {/* Credential */}
        <div className="home__m-cred">
          University of Jordan · Shabakkat KSA · 2024–Present
        </div>

        {/* Bio — short on mobile */}
        <p className="home__m-bio">
          Software Engineer Intern at <strong>Shabakkat KSA</strong>. I build
          fast, accessible React interfaces and ship real sites for
          organizations and events — <strong>IEEE CIS, TEDx, and AI Expo.</strong>
        </p>

        {/* Stat strip */}
        <div className="home__m-stats">
          {stats.map(({ n, l }) => (
            <div key={l} className="home__m-stat">
              <span className="home__m-stat-n">{n}</span>
              <span className="home__m-stat-l">{l}</span>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div className="home__m-ctas">
          <a href="#work" className="btn btn--primary btn--block">
            View My Work
            <svg className="btn__icon btn__icon--arrow" viewBox="0 0 16 16"
              fill="none" aria-hidden="true">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor"
                strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
          <div className="home__m-cta-row">
            <a href="#contact" className="btn btn--secondary btn--grow">
              Get in Touch
            </a>
            <a href="/CV.pdf" target="_blank" rel="noopener noreferrer"
              className="btn btn--ghost" download>
              <svg className="btn__icon btn__icon--down" viewBox="0 0 18 18"
                fill="none" aria-hidden="true">
                <path d="M9 3v8M6 8l3 3 3-3M4 14h10" stroke="currentColor"
                  strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              CV
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}